import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useDarkMode from '../../hooks/useDarkMode';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggle } = useDarkMode();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white dark:bg-gray-900 shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 z-50">
            <img
              src="https://static.readdy.ai/image/b38d0475ce589291eceb67661996448f/3c37db76e3e907da977a289ded40f172.png"
              alt="Daniel Carvalho Imobiliário"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Início
            </Link>

            <Link
              to="/contato"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/contato') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Contato
            </Link>

            <Link
              to="/imoveis"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/imoveis') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Imóveis
            </Link>

            <Link
              to="/carteira-ativos"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/carteira-ativos') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Carteira
            </Link>

            <Link
              to="/guia"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/guia') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Guia
            </Link>

            <Link
              to="/blog"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                location.pathname.startsWith('/blog') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Blog
            </Link>

            <Link
              to="/calculadora-valor-imovel"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/calculadora-valor-imovel') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Calculadora
            </Link>

            <Link
              to="/proprietarios"
              className={`text-sm font-semibold transition-colors hover:text-teal-600 ${
                isActive('/proprietarios') ? 'text-teal-600' : isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'
              }`}
            >
              Sou Proprietário
            </Link>

            <LanguageSwitcher />

            {/* Dark Mode Toggle */}
            <button
              onClick={toggle}
              aria-label="Alternar modo escuro"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                isScrolled
                  ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-yellow-300'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
            >
              <i className={`text-lg ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
            </button>

            <a
              href="https://wa.me/5511922305730?text=Olá,%20Daniel.%20Cheguei%20aqui%20através%20do%20seu%20site.%20Você%20pode%20me%20ajudar?"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-all hover:shadow-lg whitespace-nowrap"
            >
              Falar Agora
            </a>
          </div>

          {/* Mobile right side */}
          <div className="lg:hidden flex items-center gap-2 z-50">
            {/* Dark Mode Toggle Mobile */}
            <button
              onClick={toggle}
              aria-label="Alternar modo escuro"
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all cursor-pointer active:scale-95 ${
                isScrolled
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-yellow-300'
                  : 'bg-white/20 text-white'
              }`}
            >
              <i className={`text-lg ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-full active:scale-95 transition-transform cursor-pointer"
              aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile Menu — Slide-in overlay */}
    <div
      className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
        isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => setIsMobileMenuOpen(false)}
      />
      {/* Panel */}
      <div
        className={`absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto mobile-menu-panel transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="pt-24 pb-8 px-5">
          {/* Close button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            aria-label="Fechar menu"
          >
            <i className="ri-close-line text-2xl text-gray-700 dark:text-gray-200"></i>
          </button>

          {/* Nav items */}
          <div className="space-y-1">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-home-line text-lg text-teal-500"></i>
              Início
            </Link>
            <Link to="/contato" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-contacts-line text-lg text-teal-500"></i>
              Contato
            </Link>
            <Link to="/imoveis" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-building-line text-lg text-teal-500"></i>
              Imóveis
            </Link>
            <Link to="/carteira-ativos" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-funds-line text-lg text-teal-500"></i>
              Carteira de Ativos
            </Link>
            <Link to="/guia" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-map-2-line text-lg text-teal-500"></i>
              Guia
            </Link>
            <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-article-line text-lg text-teal-500"></i>
              Blog
            </Link>
            <Link to="/proprietarios" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-user-star-line text-lg text-teal-500"></i>
              Sou Proprietário
            </Link>
            <Link to="/calculadora-valor-imovel" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-gray-800 dark:text-gray-100 hover:bg-teal-50 dark:hover:bg-teal-900/40 hover:text-teal-600 font-semibold rounded-lg transition-colors min-h-[44px]">
              <i className="ri-calculator-line text-lg text-teal-500"></i>
              Calculadora de Valor
            </Link>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700 my-3" />

          <div className="px-3">
            <p className="text-xs text-gray-400 font-medium mb-2">Idioma</p>
            <LanguageSwitcher />
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700 my-3" />

          {/* WhatsApp CTA */}
          <a
            href="https://wa.me/5511922305730?text=Olá,%20Daniel.%20Cheguei%20aqui%20através%20do%20seu%20site.%20Você%20pode%20me%20ajudar?"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 mt-6 bg-teal-600 text-white px-6 py-4 rounded-xl font-bold text-center hover:bg-teal-700 transition-all active:scale-[0.98] shadow-lg shadow-teal-600/20"
          >
            <i className="ri-whatsapp-fill text-xl"></i>
            Falar Agora
          </a>
        </div>
      </div>
    </div>
    </>
  );
};

export default Navbar;